import type { Locale } from "./locales";

/**
 * 숫자·시각·목록 표기 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * 표기는 **브라우저 내장 Intl**만 씁니다. 자리 구분·시간대·목록 접속사 규칙을
 * 직접 구현하면 언어를 더할 때마다 규칙이 흩어지고, 번들만 커집니다.
 *
 * Formatter 인스턴스는 만드는 비용이 커서 캐시합니다. 키 공간은
 * (언어 × 호출부가 쓰는 옵션 조합)이라 유계이고, 상한을 넘으면 캐시를 비웁니다 —
 * 무한히 자라는 전역 맵을 남기지 않습니다.
 */
const MAX_CACHED_FORMATTERS = 64;
const cache = new Map<string, unknown>();

function cached<T>(key: string, make: () => T): T {
  const hit = cache.get(key);
  if (hit !== undefined) return hit as T;
  if (cache.size >= MAX_CACHED_FORMATTERS) cache.clear();
  const value = make();
  cache.set(key, value);
  return value;
}

/** 자리 구분이 언어를 따릅니다. Kubernetes 이름·UID는 숫자가 아니므로 지나가지 않습니다. */
export function formatNumber(locale: Locale, value: number, options?: Intl.NumberFormatOptions): string {
  const key = `n:${locale}:${options ? JSON.stringify(options) : ""}`;
  return cached(key, () => new Intl.NumberFormat(locale, options)).format(value);
}

/** RFC3339 문자열 또는 Date를 사용자 시간대로 표기합니다. 잘못된 값은 원문 그대로 둡니다. */
export function formatDateTime(locale: Locale, value: string | number | Date, options?: Intl.DateTimeFormatOptions): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const key = `d:${locale}:${options ? JSON.stringify(options) : ""}`;
  return cached(key, () => new Intl.DateTimeFormat(locale, options ?? { dateStyle: "medium", timeStyle: "medium" })).format(date);
}

/** 목록 접속사도 언어를 따릅니다. */
export function formatList(locale: Locale, items: readonly string[], type: Intl.ListFormatType = "conjunction"): string {
  const key = `l:${locale}:${type}`;
  return cached(key, () => new Intl.ListFormat(locale, { type, style: "long" })).format(items);
}

/** 테스트 전용 — formatter 캐시를 비웁니다. */
export function resetFormattersForTest(): void {
  cache.clear();
}
